import React, { useState } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  Select,
  Option,
  Checkbox,
  Typography,
  Box,
  Alert,
} from '@mui/joy';
import { ChatCircle as MessageSquare } from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import type { OpportunityNote } from '../../types';

interface OpportunityNoteModalProps {
  open: boolean;
  onClose: () => void;
  opportunityId: number;
}

export const OpportunityNoteModal: React.FC<OpportunityNoteModalProps> = ({
  open,
  onClose,
  opportunityId,
}) => {
  const queryClient = useQueryClient();
  const [content, setContent] = useState('');
  const [noteType, setNoteType] = useState<OpportunityNote['note_type']>('general');
  const [isPrivate, setIsPrivate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createNoteMutation = useMutation({
    mutationFn: async (data: Partial<OpportunityNote>) => {
      const response = await apiClient.post<OpportunityNote>('/api/crm/opportunity-notes/', data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the opportunity detail query to refresh the notes
      queryClient.invalidateQueries({ queryKey: ['opportunity', opportunityId.toString()] });
      // Reset form and close modal
      setContent('');
      setNoteType('general');
      setIsPrivate(false);
      setError(null);
      onClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to create note');
    },
  });

  const handleSubmit = () => {
    if (!content.trim()) {
      setError('Note content is required');
      return;
    }

    createNoteMutation.mutate({
      opportunity: opportunityId,
      content: content.trim(),
      note_type: noteType,
      is_private: isPrivate,
    });
  };

  const handleClose = () => {
    setContent('');
    setNoteType('general');
    setIsPrivate(false);
    setError(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ width: 500, maxWidth: '90vw' }}>
        <ModalClose />
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MessageSquare size={20} />
            Add Note
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && (
              <Alert 
                color="danger" 
                endDecorator={
                  <Button 
                    variant="plain" 
                    size="sm" 
                    color="danger" 
                    onClick={() => setError(null)}
                  >
                    Dismiss
                  </Button>
                }
              >
                {error}
              </Alert>
            )}
            
            <FormControl required>
              <FormLabel>Note Content</FormLabel>
              <Textarea
                placeholder="Enter your note here..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                minRows={4}
                maxRows={10}
                sx={{ fontSize: 'sm' }}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Note Type</FormLabel>
              <Select
                value={noteType}
                onChange={(_, value) => value && setNoteType(value as OpportunityNote['note_type'])}
              >
                <Option value="general">General Note</Option>
                <Option value="call">Phone Call</Option>
                <Option value="meeting">Meeting</Option>
                <Option value="email">Email</Option>
                <Option value="follow_up">Follow-up</Option>
              </Select>
            </FormControl>

            <FormControl>
              <Checkbox
                label="Private note (only visible to me)"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
              />
            </FormControl>

            <Typography level="body-xs" sx={{ color: 'text.secondary', mt: -1 }}>
              {isPrivate
                ? 'This note will only be visible to you'
                : 'This note will be visible to all team members'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="plain" color="neutral" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            onClick={handleSubmit}
            loading={createNoteMutation.isPending}
            disabled={!content.trim()}
          >
            Add Note
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};